import React, { useCallback, useState, useEffect, createContext, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { AlertTriangle } from 'lucide-react';

// Global context for unsaved changes detection
const UnsavedContext = createContext(null);

export const UnsavedChangesProvider = ({ children }) => {
  const checkerRef = useRef(null);

  const register = useCallback((checker) => {
    checkerRef.current = checker;
  }, []);

  const unregister = useCallback(() => {
    checkerRef.current = null;
  }, []);

  const check = useCallback(() => {
    return checkerRef.current ? checkerRef.current() : null;
  }, []);

  return (
    <UnsavedContext.Provider value={{ register, unregister, check }}>
      {children}
    </UnsavedContext.Provider>
  );
};

export const useUnsavedContext = () => useContext(UnsavedContext);

export const UnsavedChangesDialog = ({ 
  open, 
  onOpenChange, 
  onSave, 
  onDiscard,
  saving = false 
}) => {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-warning" />
            </div>
            <AlertDialogTitle>Cambios sin guardar</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            Tienes cambios que no has guardado. ¿Qué deseas hacer?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel onClick={onDiscard}>
            Salir sin guardar
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar y continuar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Custom hook to track unsaved changes
export const useUnsavedChanges = (initialData, currentData) => {
  const hasChanges = React.useMemo(() => {
    if (!initialData || !currentData) return false;
    return JSON.stringify(initialData) !== JSON.stringify(currentData);
  }, [initialData, currentData]);
  
  return hasChanges;
};

// Hook: register page-level unsaved check with global context + beforeunload
export const useUnsavedBlocker = (hasUnsavedChanges, onSave) => {
  const ctx = useUnsavedContext();
  const [showDialog, setShowDialog] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);
  const navigate = useNavigate();
  const saveFnRef = useRef(onSave);
  saveFnRef.current = onSave;
  const hasChangesRef = useRef(hasUnsavedChanges);
  hasChangesRef.current = hasUnsavedChanges;

  // Register with global context
  useEffect(() => {
    if (!ctx) return;
    ctx.register(() => {
      if (hasChangesRef.current) return { save: saveFnRef.current };
      return null;
    });
    return () => ctx.unregister();
  }, [ctx]);

  // Browser beforeunload
  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  const tryNavigate = useCallback((path) => {
    if (hasChangesRef.current) {
      setPendingPath(path);
      setShowDialog(true);
      return true; // blocked
    }
    return false; // not blocked
  }, []);

  const handleSaveAndProceed = async () => {
    if (saveFnRef.current) await saveFnRef.current();
    setShowDialog(false);
    if (pendingPath) navigate(pendingPath);
    setPendingPath(null);
  };

  const handleDiscard = () => {
    setShowDialog(false);
    if (pendingPath) navigate(pendingPath);
    setPendingPath(null);
  };

  const handleCancel = () => {
    setShowDialog(false);
    setPendingPath(null);
  };

  return { showDialog, tryNavigate, handleSaveAndProceed, handleDiscard, handleCancel };
};

// Ready-made dialog component
export const BlockerDialog = ({ showDialog, onSave, onDiscard, onCancel, saving = false }) => {
  if (!showDialog) return null;

  return (
    <AlertDialog open={true} onOpenChange={onCancel}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-warning" />
            </div>
            <AlertDialogTitle>Cambios sin guardar</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-2">
            Tienes cambios que no has guardado. ¿Qué deseas hacer?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel onClick={onDiscard}>
            Salir sin guardar
          </AlertDialogCancel>
          <AlertDialogAction onClick={onSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar y continuar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
